import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { 
  AlertTriangle, Package, Clock, X, Bell, 
  AlertCircle, ChevronDown, ChevronUp 
} from "lucide-react";
import { Link } from "react-router-dom";

const API = window.location.origin + "/api";

export default function InventoryAlerts({ compact = false }) {
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(!compact);
  const [dismissed, setDismissed] = useState([]);

  useEffect(() => {
    fetchAlerts();
    // تحديث كل 5 دقائق
    const interval = setInterval(fetchAlerts, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchAlerts = async () => {
    try {
      const response = await axios.get(`${API}/products/inventory-alerts`);
      setAlerts(response.data);
    } catch (error) {
      console.error("Error fetching alerts:", error);
    } finally {
      setLoading(false);
    }
  };

  const dismissAlert = (id) => {
    setDismissed([...dismissed, id]);
  };

  if (loading) return null;
  if (!alerts || alerts.summary?.total_alerts === 0) return null;

  const visibleAlerts = {
    out_of_stock: alerts.out_of_stock?.filter(a => !dismissed.includes(a.id)) || [],
    low_stock: alerts.low_stock?.filter(a => !dismissed.includes(a.id)) || [],
    expired: alerts.expired?.filter(a => !dismissed.includes(a.id)) || [],
    expiring_soon: alerts.expiring_soon?.filter(a => !dismissed.includes(a.id)) || [],
  };

  const totalVisible = 
    visibleAlerts.out_of_stock.length + 
    visibleAlerts.low_stock.length + 
    visibleAlerts.expired.length + 
    visibleAlerts.expiring_soon.length;

  if (totalVisible === 0) return null;

  // النسخة المصغرة للـ Dashboard
  if (compact && !expanded) {
    return (
      <Card className="bg-gradient-to-br from-amber-500/10 to-red-500/10 border-amber-500/30">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                <Bell className="w-5 h-5 text-amber-500 animate-pulse" />
              </div>
              <div>
                <p className="text-white font-bold">{totalVisible} تنبيه مخزون</p>
                <p className="text-slate-400 text-sm">
                  {visibleAlerts.out_of_stock.length > 0 && `${visibleAlerts.out_of_stock.length} نفذ`}
                  {visibleAlerts.low_stock.length > 0 && ` • ${visibleAlerts.low_stock.length} منخفض`}
                </p>
              </div>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setExpanded(true)}
              className="text-amber-400"
            >
              <ChevronDown className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-slate-800/50 border-slate-700">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            تنبيهات المخزون
            <span className="bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">
              {totalVisible}
            </span>
          </CardTitle>
          {compact && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setExpanded(false)}
              className="text-slate-400"
            >
              <ChevronUp className="w-4 h-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* نفذ من المخزون */}
        {visibleAlerts.out_of_stock.length > 0 && (
          <div className="space-y-2">
            <p className="text-red-400 text-sm font-bold flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              نفذ من المخزون ({visibleAlerts.out_of_stock.length})
            </p>
            {visibleAlerts.out_of_stock.slice(0, 5).map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between bg-red-500/10 border border-red-500/30 rounded-lg p-2"
              >
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-red-500" />
                  <span className="text-white text-sm">{item.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-red-400 text-xs">0 قطعة</span>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => dismissAlert(item.id)}
                    className="h-6 w-6 text-slate-500 hover:text-white"
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* مخزون منخفض */}
        {visibleAlerts.low_stock.length > 0 && (
          <div className="space-y-2">
            <p className="text-amber-400 text-sm font-bold flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" />
              مخزون منخفض ({visibleAlerts.low_stock.length})
            </p>
            {visibleAlerts.low_stock.slice(0, 5).map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between bg-amber-500/10 border border-amber-500/30 rounded-lg p-2"
              >
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-amber-500" />
                  <span className="text-white text-sm">{item.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-amber-400 text-xs">{item.stock} / {item.min_stock}</span>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => dismissAlert(item.id)}
                    className="h-6 w-6 text-slate-500 hover:text-white"
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* منتهي الصلاحية */}
        {visibleAlerts.expired.length > 0 && (
          <div className="space-y-2">
            <p className="text-red-400 text-sm font-bold flex items-center gap-1">
              <Clock className="w-4 h-4" />
              منتهي الصلاحية ({visibleAlerts.expired.length})
            </p>
            {visibleAlerts.expired.slice(0, 3).map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between bg-red-500/10 border border-red-500/30 rounded-lg p-2"
              >
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-red-500" />
                  <span className="text-white text-sm">{item.name}</span>
                </div>
                <span className="text-red-400 text-xs">{item.expiry_date}</span>
              </div>
            ))}
          </div>
        )}

        {/* قريب الانتهاء */}
        {visibleAlerts.expiring_soon.length > 0 && (
          <div className="space-y-2">
            <p className="text-orange-400 text-sm font-bold flex items-center gap-1">
              <Clock className="w-4 h-4" />
              قريب الانتهاء ({visibleAlerts.expiring_soon.length})
            </p>
            {visibleAlerts.expiring_soon.slice(0, 3).map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between bg-orange-500/10 border border-orange-500/30 rounded-lg p-2"
              >
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-orange-500" />
                  <span className="text-white text-sm">{item.name}</span>
                </div>
                <span className="text-orange-400 text-xs">{item.expiry_date}</span>
              </div>
            ))}
          </div>
        )}

        {/* رابط للمخزون */}
        <Link to="/inventory">
          <Button variant="outline" className="w-full mt-2 border-slate-600 text-slate-300 hover:bg-slate-700">
            عرض المخزون الكامل
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}
